import {
  formatParisDateTime,
  KLAVIYO_CATEGORY_PROPERTY,
  RECHARGE_FEATURE_MODE,
  RECHARGE_RUN_STATUS,
  type KlaviyoSyncListItem,
  type Paginated,
  type RechargeFeatureMode,
  type RechargeSettingsSummary,
} from '@flux/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useState, type JSX } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { useKlaviyoSummary } from '../settings/api-klaviyo.js';
import { fetchKlaviyoSyncs, startKlaviyoSync } from './api.js';
import { useDetailSheetParam } from './detail-sheet.js';
import { KlaviyoSyncDetailSheet } from './klaviyo-sync-detail-sheet.js';
import { formatCount } from './money.js';
import {
  isRechargeTokenMissing,
  ModeBanner,
  RECHARGE_NOT_CONFIGURED_EMPTY_STATE,
  useRechargeSummary,
} from './recharge-status.js';

const PAGE_SIZE = 50;
/** Rafraîchissement de la liste tant qu'une synchronisation tourne. */
const POLL_INTERVAL_MS = 2_000;
const NUMERIC_COLUMNS = ['subscribers', 'set', 'unset', 'errors'] as const;
const START_LABEL = 'Synchroniser maintenant';
const START_REASON_ID = 'klaviyo-sync-start-reason';

const BANNER_COPY: Readonly<Record<Exclude<RechargeFeatureMode, 'active'>, { title: string; body: string }>> = {
  simulation: {
    title: 'Simulation',
    body: 'Flux calcule chaque nuit les changements mais n’écrit rien chez Klaviyo',
  },
  disabled: {
    title: 'Synchronisation désactivée',
    body: 'Aucune synchronisation de nuit ; une simulation reste possible à la main',
  },
};

/** Mode effectif : le mode test global force la simulation (`06` §0). */
export function effectiveKlaviyoMode(summary: RechargeSettingsSummary): RechargeFeatureMode {
  const mode = summary.klaviyo.mode;
  return summary.testMode && mode !== 'disabled' ? 'simulation' : mode;
}

function hasRunningSync(page: Paginated<KlaviyoSyncListItem> | undefined): boolean {
  return page?.items.some((item) => item.status === 'running') ?? false;
}

function Count({ value }: { value: number | undefined }): JSX.Element {
  return value === undefined ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="whitespace-nowrap tabular-nums">{formatCount(value)}</span>
  );
}

const helper = createDataTableColumnHelper<KlaviyoSyncListItem>();

const columns = helper.columns([
  helper.accessor('startedAt', {
    id: 'startedAt',
    header: 'Lancée le',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">{formatParisDateTime(new Date(info.getValue()))}</span>
    ),
  }),
  helper.display({
    id: 'trigger',
    header: 'Déclenchement',
    cell: ({ row }) =>
      row.original.trigger === 'schedule' ? 'Nuit' : (row.original.startedBy?.name ?? 'Manuel'),
  }),
  helper.accessor('mode', {
    id: 'mode',
    header: 'Mode',
    cell: (info) => <StatusBadge registry={RECHARGE_FEATURE_MODE} code={info.getValue()} />,
  }),
  helper.accessor('status', {
    id: 'status',
    header: 'Statut',
    cell: ({ row }) => {
      const { status, error } = row.original;
      return (
        <span className="flex min-w-0 flex-col items-start gap-1">
          <StatusBadge registry={RECHARGE_RUN_STATUS} code={status} />
          {status === 'failed' && error !== null ? (
            <span className="block max-w-xs truncate text-xs text-status-red-fg" title={error}>
              {error}
            </span>
          ) : null}
        </span>
      );
    },
  }),
  helper.display({
    id: 'subscribers',
    header: 'Abonnés',
    cell: ({ row }) => <Count value={row.original.counts.subscribers} />,
  }),
  helper.display({
    id: 'set',
    header: 'Posées',
    cell: ({ row }) => <Count value={row.original.counts.set} />,
  }),
  helper.display({
    id: 'unset',
    header: 'Retirées',
    cell: ({ row }) => <Count value={row.original.counts.unset} />,
  }),
  helper.display({
    id: 'errors',
    header: 'Erreurs',
    cell: ({ row }) => <Count value={row.original.counts.errors} />,
  }),
]);

/**
 * Onglet Klaviyo de l'écran Abonnements (`06` §11) : historique des
 * synchronisations de la catégorie d'abonné, lancement à la demande, détail
 * des profils modifiés dans un panneau porté par `?id=`.
 */
export function KlaviyoTab(): JSX.Element {
  const queryClient = useQueryClient();
  const { page, setPage } = useDataTableUrlState();
  const { id, openDetail, closeDetail } = useDetailSheetParam();
  const summary = useRechargeSummary();
  const klaviyo = useKlaviyoSummary();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [simulation, setSimulation] = useState(true);
  const [startError, setStartError] = useState<string | null>(null);
  const noteId = useId();

  const listParams = { page, pageSize: PAGE_SIZE };
  const query = useQuery({
    queryKey: queryKeys.subscriptions.klaviyoSyncs(listParams),
    queryFn: () => fetchKlaviyoSyncs(listParams),
    placeholderData: keepPreviousData,
    refetchInterval: (current) => (hasRunningSync(current.state.data) ? POLL_INTERVAL_MS : false),
  });

  const start = useMutation({
    mutationFn: startKlaviyoSync,
    onSuccess: (item) => {
      setConfirmOpen(false);
      toast.success(item.mode === 'simulation' ? 'Simulation lancée' : 'Synchronisation lancée');
      if (page !== 1) {
        setPage(1);
      }
    },
    onError: (error) => {
      setStartError(error instanceof ApiError ? error.message : 'Impossible de lancer la synchronisation.');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.klaviyoSyncsAll });
    },
  });

  const settings = summary.data;
  const tokenMissing = isRechargeTokenMissing(settings);
  const keyMissing = klaviyo.data?.apiKeyConfigured === false;
  const mode = settings ? effectiveKlaviyoMode(settings) : null;
  const forcedSimulation = mode !== 'active';
  const running = hasRunningSync(query.data);
  const disabledReason = tokenMissing
    ? 'Ajoutez le token API Recharge dans Paramètres'
    : keyMissing
      ? 'Ajoutez la clé API Klaviyo dans Paramètres'
      : running
        ? 'Une synchronisation est en cours'
        : null;
  const canStart = settings !== undefined && klaviyo.data !== undefined && disabledReason === null && !start.isPending;

  function openConfirm(): void {
    setSimulation(true);
    setStartError(null);
    setConfirmOpen(true);
  }

  return (
    <>
      {mode === null || mode === 'active' ? null : <ModeBanner mode={mode} copy={BANNER_COPY[mode]} />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Propriété{' '}
          <code className="font-mono text-xs" translate="no">
            {KLAVIYO_CATEGORY_PROPERTY}
          </code>{' '}
          sur les profils des abonnés actifs · chaque nuit à 4 h
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {disabledReason === null ? null : (
            <p id={START_REASON_ID} className="text-sm text-muted-foreground">
              {disabledReason}
            </p>
          )}
          <Button
            type="button"
            disabled={!canStart}
            isLoading={running || start.isPending}
            aria-describedby={disabledReason === null ? undefined : START_REASON_ID}
            onClick={openConfirm}
          >
            {START_LABEL}
          </Button>
        </div>
      </div>
      <DataTable
        caption="Synchronisations Klaviyo"
        columns={columns}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        numericColumnIds={NUMERIC_COLUMNS}
        onPageChange={setPage}
        onRowClick={(row) => {
          openDetail(row.id);
        }}
        isLoading={query.isPending}
        isError={query.isError}
        errorMessage={
          query.error instanceof ApiError ? query.error.message : 'Impossible de charger les synchronisations.'
        }
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        emptyState={
          tokenMissing
            ? RECHARGE_NOT_CONFIGURED_EMPTY_STATE
            : keyMissing
              ? {
                  title: 'Connectez Klaviyo pour commencer',
                  description: 'Ajoutez la clé API Klaviyo dans Paramètres.',
                  action: (
                    <Button asChild variant="outline">
                      <Link to="/parametres" search={{ tab: 'klaviyo' }}>
                        Ouvrir les réglages Klaviyo
                      </Link>
                    </Button>
                  ),
                }
              : {
                  title: 'Aucune synchronisation',
                  description: 'Lancez une simulation pour voir les profils Klaviyo que Flux mettrait à jour.',
                  action: (
                    <Button type="button" variant="outline" disabled={!canStart} onClick={openConfirm}>
                      {START_LABEL}
                    </Button>
                  ),
                }
        }
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open && !start.isPending) {
            setConfirmOpen(false);
          }
        }}
        title="Synchroniser Klaviyo ?"
        description={
          <div className="flex flex-col gap-3">
            <p>
              Flux relit les abonnements actifs chez Recharge (environ une minute), puis aligne la catégorie
              d’abonné des profils Klaviyo. Seuls les écarts sont écrits.
            </p>
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <Checkbox
                  checked={forcedSimulation || simulation}
                  disabled={forcedSimulation}
                  aria-describedby={forcedSimulation ? noteId : undefined}
                  onCheckedChange={(checked) => {
                    setSimulation(checked === true);
                  }}
                />
                Simulation : ne rien écrire chez Klaviyo
              </label>
              {forcedSimulation ? (
                <p id={noteId} className="pl-6 text-xs text-muted-foreground">
                  {settings?.testMode
                    ? 'Le mode test de Flux force la simulation'
                    : 'Passez la synchronisation en Actif dans Paramètres pour écrire chez Klaviyo'}
                </p>
              ) : null}
            </div>
            {startError === null ? null : (
              <p role="alert" className="text-sm text-status-red-fg">
                {startError}
              </p>
            )}
          </div>
        }
        confirmLabel={forcedSimulation || simulation ? 'Lancer la simulation' : 'Lancer la synchronisation'}
        confirmVariant="default"
        isConfirming={start.isPending}
        onConfirm={() => {
          setStartError(null);
          start.mutate({ simulation: forcedSimulation || simulation });
        }}
      />
      <KlaviyoSyncDetailSheet id={id} onClose={closeDetail} />
    </>
  );
}
